import { useState, useEffect } from 'react'
import { Plus, Trash2, Pencil, Clock, Calendar, ChevronDown, ChevronRight } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { useTimer } from './TaskTimer'
import { TimeEntryModal, PAY_LEVEL_LABEL, hm, serviceAreaLabel } from './TimeEntryShared'
import {
  recentPeriods, periodByIndex, periodIndexFor, currentPeriod, periodState, PERIOD_STATE,
  totalsByBucket, fmtHours, fmtRange, fmtDay, inPeriod,
} from './payPeriods'

function fmtDate(d) {
  if (!d) return ''
  try { return format(new Date(d + 'T12:00:00'), 'EEE, MMM d, yyyy') } catch { return d }
}

// Draft rows show a friendly "started at h:mm a" instead of a duration.
function fmtStartedAt(iso) {
  if (!iso) return ''
  try { return format(new Date(iso), 'h:mm a') } catch { return '' }
}


// The payroll view: periods, pay dates, and hours split by pay level — the
// same shape as the payroll sheet, so nobody has to translate between them.
function PayPeriodTable({ periods, entries, expanded, onExpand }) {
  return (
    <div className="card" style={{ marginBottom: 14, overflowX: 'auto' }}>
      <table className="task-table" style={{ width: '100%', minWidth: 620, fontSize: 13 }}>
        <thead>
          <tr>
            <th colSpan={2} style={{ textAlign: 'center', background: 'var(--lightgreen)',
              color: 'var(--green)' }}>Pay Period</th>
            <th style={{ textAlign: 'center' }}>Pay Date</th>
            <th style={{ textAlign: 'right' }}>Total Hours AM</th>
            <th style={{ textAlign: 'right' }}>Total Hours AD</th>
            <th style={{ textAlign: 'right' }}>Total</th>
            <th style={{ width: 30 }}></th>
          </tr>
          <tr>
            <th style={{ fontWeight: 600 }}>Start Date</th>
            <th style={{ fontWeight: 600 }}>End Date</th>
            <th colSpan={5}></th>
          </tr>
        </thead>
        <tbody>
          {periods.map(p => {
            const rows = entries.filter(e => inPeriod(e.entry_date, p))
            const t = totalsByBucket(rows)
            const st = periodState(p)
            const style = PERIOD_STATE[st]
            const open = expanded === p.index
            return (
              <tr key={p.index} onClick={() => onExpand(open ? null : p.index)}
                style={{ cursor: 'pointer',
                  background: open ? 'var(--cream-dark)' : style.bg !== 'transparent' ? style.bg : undefined }}>
                <td>{fmtDay(p.start)}</td>
                <td>{fmtDay(p.end)}</td>
                <td style={{ textAlign: 'center' }}>
                  {fmtDay(p.payDate)}
                  <div style={{ fontSize: 10.5, color: style.color, fontWeight: 600 }}>{style.label}</div>
                </td>
                <td style={{ textAlign: 'right' }}>{fmtHours(t.am)}</td>
                <td style={{ textAlign: 'right' }}>{fmtHours(t.ad)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {fmtHours(t.total)}
                  {t.other > 0 && (
                    <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}
                      title="Hours logged at technician or supervising level">
                      incl. {fmtHours(t.other)} other
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── "My Time" view ────────────────────────────────────────────────────────────
export default function TimeTrackingView({ allClients, tasks, currentUser, profile }) {
  const toast = useToast()
  const timerCtx = useTimer()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)

  // Default is the payroll view. Custom lets people pick their own dates,
  // which was the other half of what was asked for.
  const [mode, setMode] = useState('periods')       // 'periods' | 'custom'
  const [periodCount, setPeriodCount] = useState(6)
  const [expandedPeriod, setExpandedPeriod] = useState(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const clientName = id => allClients.find(c => c.id === id)?.name || '—'
  const taskTitle = id => tasks.find(t => t.id === id)?.title || null

  const load = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('entry_date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(2000)
    setLoading(false)
    if (error) { toast('Error loading time: ' + error.message, 'error'); return }
    setEntries(data || [])
  }

  useEffect(() => { load() }, [])

  const remove = async entry => {
    if (!window.confirm(entry.is_manual_draft && !entry.ended_at ? 'Discard this draft entry?' : 'Delete this time entry?')) return
    const { error } = await supabase.from('time_entries').delete().eq('id', entry.id)
    if (error) { toast('Error deleting: ' + error.message, 'error'); return }
    toast(entry.is_manual_draft && !entry.ended_at ? 'Draft discarded' : 'Entry deleted', 'success')
    load()
    timerCtx?.refresh?.()
  }

  const onSaved = () => { setShowModal(false); setEditing(null); load(); timerCtx?.refresh?.() }

  const periods = recentPeriods(periodCount)

  // Which entries the list below shows.
  //   periods mode — the expanded period, or everything in the visible periods
  //   custom mode  — whatever range they picked
  const visible = (() => {
    if (mode === 'custom') {
      if (!from && !to) return entries
      return entries.filter(e =>
        (!from || e.entry_date >= from) && (!to || e.entry_date <= to))
    }
    if (expandedPeriod !== null) {
      const p = periodByIndex(expandedPeriod)
      return entries.filter(e => inPeriod(e.entry_date, p))
    }
    const oldest = periods[periods.length - 1]
    const newest = periods[0]
    return entries.filter(e => e.entry_date >= oldest.start && e.entry_date <= newest.end)
  })()

  // Group by date for display
  const groups = []
  const byDate = {}
  visible.forEach(e => {
    if (!byDate[e.entry_date]) { byDate[e.entry_date] = []; groups.push(e.entry_date) }
    byDate[e.entry_date].push(e)
  })
  const totalSeconds = visible.reduce((sum, e) => sum + (e.duration_seconds || 0), 0)
  const draftCount = entries.filter(e => e.is_manual_draft && !e.ended_at).length
  const visTotals = totalsByBucket(visible)

  return (
    <div>
      {/* Payroll view by default; custom range for anyone who wants their own dates */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="tabs">
          <button className={`tab ${mode === 'periods' ? 'active' : ''}`}
            onClick={() => { setMode('periods'); setExpandedPeriod(null) }}>
            <Calendar size={12} /> Pay periods
          </button>
          <button className={`tab ${mode === 'custom' ? 'active' : ''}`}
            onClick={() => setMode('custom')}>
            Custom dates
          </button>
        </div>

        {mode === 'periods' && (
          <select className="filter-select" value={periodCount}
            onChange={e => setPeriodCount(Number(e.target.value))}>
            <option value={3}>Last 3 periods</option>
            <option value={6}>Last 6 periods</option>
            <option value={13}>Last 13 periods</option>
            <option value={26}>Last 26 periods</option>
          </select>
        )}

        {mode === 'custom' && (
          <>
            <input className="filter-select" type="date" value={from}
              onChange={e => setFrom(e.target.value)} title="From" />
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
            <input className="filter-select" type="date" value={to}
              onChange={e => setTo(e.target.value)} title="To" />
            {(from || to) && (
              <button className="btn btn-ghost btn-sm"
                onClick={() => { setFrom(''); setTo('') }}>Clear</button>
            )}
            <button className="btn btn-ghost btn-sm" title="Fill in the current pay period"
              onClick={() => { const p = currentPeriod(); setFrom(p.start); setTo(p.end) }}>
              This pay period
            </button>
          </>
        )}

        <button className="btn btn-gold" style={{ marginLeft: 'auto' }}
          onClick={() => { setEditing(null); setShowModal(true) }}>
          <Plus size={16} /> Log time
        </button>
      </div>

      {mode === 'periods' && (
        <PayPeriodTable periods={periods} entries={entries}
          expanded={expandedPeriod} onExpand={setExpandedPeriod} />
      )}

      {/* What the list below is showing, and its totals */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
        flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: 13.5 }}>
        <span>
          {mode === 'periods' && expandedPeriod !== null
            ? `${fmtRange(periodByIndex(expandedPeriod))} · `
            : mode === 'custom' && (from || to)
              ? `${from ? fmtDay(from) : 'anything'} to ${to ? fmtDay(to) : 'today'} · `
              : ''}
          {visible.length} {visible.length === 1 ? 'entry' : 'entries'} · {hm(totalSeconds)} total
        </span>
        {visTotals.am > 0 && <span>AM {fmtHours(visTotals.am)}h</span>}
        {visTotals.ad > 0 && <span>AD {fmtHours(visTotals.ad)}h</span>}
        {draftCount > 0 && (
          <span style={{ color: 'var(--gold)', fontWeight: 600 }}>
            {draftCount} draft{draftCount === 1 ? '' : 's'} in progress
          </span>
        )}
        {mode === 'periods' && expandedPeriod !== null && (
          <button className="btn btn-ghost btn-sm" onClick={() => setExpandedPeriod(null)}>
            Show all periods
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: 24 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Clock size={28} /></div>
          <div>
            {entries.length === 0
              ? <>No time logged yet. Click <strong>Log time</strong> to add an entry.</>
              : 'No entries in this range.'}
          </div>
        </div>
      ) : groups.map(d => {
        const rows = byDate[d]
        const dayTotal = rows.reduce((s, e) => s + (e.duration_seconds || 0), 0)
        return (
          <div className="card" key={d} style={{ marginBottom: 14 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="card-title">{fmtDate(d)}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{hm(dayTotal)}</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {rows.map(e => {
                const isDraft = e.is_manual_draft && !e.ended_at
                return (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderTop: '1px solid var(--border-light)', background: isDraft ? 'var(--gold-light)' : undefined }}>
                    {isDraft ? (
                      <div style={{ minWidth: 88 }}>
                        <span style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.4px', color: 'var(--gold)', border: '1px solid currentColor',
                          borderRadius: 4, padding: '2px 6px',
                        }}>In progress</span>
                      </div>
                    ) : (
                      <div style={{ minWidth: 56, fontWeight: 700, color: 'var(--text-primary)' }}>{hm(e.duration_seconds)}</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {taskTitle(e.task_id) || (e.category === 'meeting' ? 'Meeting' : e.category === 'admin' ? 'Admin' : 'Work')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {e.client_id ? clientName(e.client_id) : 'No client'}
                        {e.service_area ? ` · ${serviceAreaLabel(e.service_area)}` : ''}
                        {e.pay_level ? ` · ${PAY_LEVEL_LABEL[e.pay_level] || e.pay_level}` : ''}
                        {isDraft ? ` · Started ${fmtStartedAt(e.started_at)}` : ''}
                      </div>
                      {e.description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{e.description}</div>}
                    </div>
                    {isDraft ? (
                      <button className="btn btn-sm btn-gold" onClick={() => { setEditing(e); setShowModal(true) }}>Finish</button>
                    ) : (
                      <button className="btn-icon" title="Edit" onClick={() => { setEditing(e); setShowModal(true) }}><Pencil size={15} /></button>
                    )}
                    <button className="btn-icon" title={isDraft ? 'Discard' : 'Delete'} onClick={() => remove(e)}><Trash2 size={15} /></button>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {showModal && (
        <TimeEntryModal
          allClients={allClients}
          tasks={tasks}
          currentUser={currentUser}
          profile={profile}
          editing={editing}
          activeElsewhere={timerCtx?.activeEntry && (!editing || timerCtx.activeEntry.id !== editing.id) ? timerCtx.activeEntry : null}
          onClose={() => { setShowModal(false); setEditing(null) }}
          onSaved={onSaved}
        />
      )}
    </div>
  )
}